import React from 'react';
import { Box, TextField, Typography } from '@material-ui/core';
import { TabletButton } from '@campfire/tablet-button';
import { FullscreenDialog } from '../../../../../common/dialogs/FullscreenDialog';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';

interface Props {
  open: boolean;
  subTenantId: string;
  subAccountName: string;
  onClose: () => void;
  onInvited: () => void;
}

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export const InviteSubAccountAdminDialog = (props: Props) => {
  const { open, subTenantId, subAccountName, onClose, onInvited } = props;
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const campfireMutation = useCampfireFetch({ defer: true });

  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const reset = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setSaving(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const canSubmit = firstName.trim() !== '' && lastName.trim() !== '' && isValidEmail(email.trim());

  const handleInvite = () => {
    setSaving(true);

    campfireMutation
      .run({
        url: `${apiUrl}/tm/enterprise/sub-accounts/invite-admin`,
        method: 'post',
        data: {
          subTenantId,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
        },
      })
      .then((res: any) => {
        if (!res.ok) {
          setSaving(false);
          setSnackbar({
            variant: 'error',
            open: true,
            message: res?.data?.message || 'Could not send the invitation',
          });
          return;
        }

        setSnackbar({
          variant: 'success',
          open: true,
          message: `Invitation sent to ${email.trim()}`,
        });
        reset();
        onInvited();
        onClose();
      })
      .catch(() => {
        setSaving(false);
        setSnackbar({
          variant: 'error',
          open: true,
          message: 'Could not send the invitation',
        });
      });
  };

  if (!open) return null;

  return (
    <FullscreenDialog
      open
      close={handleClose}
      title='Invite Admin'
      maxWidth='xs'
      fullWidth
      dialogActions={() => (
        <>
          <TabletButton onClick={handleClose} disabled={saving} style={{ marginRight: '8px' }}>
            Cancel
          </TabletButton>
          <TabletButton variant='contained' color='primary' onClick={handleInvite} disabled={saving || !canSubmit}>
            {saving ? 'Sending…' : 'Send Invitation'}
          </TabletButton>
        </>
      )}
    >
      <Box mt='20px'>
        <Typography variant='body2' style={{ fontSize: '14px', color: '#808080' }}>
          {`They will get an email inviting them to set a password and administer ${subAccountName}.`}
        </Typography>

        <Box marginTop='1.25rem'>
          <TextField
            autoFocus
            fullWidth
            required
            variant='outlined'
            label='First name'
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </Box>

        <Box marginTop='1.25rem'>
          <TextField
            fullWidth
            required
            variant='outlined'
            label='Last name'
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </Box>

        <Box marginTop='1.25rem'>
          <TextField
            fullWidth
            required
            variant='outlined'
            label='Email'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            helperText='If they already have a Volaby account, it will be given access instead'
          />
        </Box>
      </Box>
    </FullscreenDialog>
  );
};
